import React, { useMemo, useState } from 'react';
import { Alert, Form, Input, Modal, Typography } from 'antd';
import { resolveDefaultTenantId } from '../../config/environment';
import { emitStepUpMfaCompleted } from '../../workbench/step-up-events';
import { getTenantId, setJWTToken, setRefreshToken } from '../../utils/jwtUtils';

const { Text } = Typography;

export interface StepUpMfaModalProps {
  open: boolean;
  onClose: () => void;
  onSucceeded?: () => void;
}

type StepUpFormValues = {
  password?: string;
  otpCode?: string;
};

function isNonProd(): boolean {
  return process.env.NODE_ENV !== 'production';
}

export function StepUpMfaModal({
  open,
  onClose,
  onSucceeded,
}: StepUpMfaModalProps): React.ReactElement {
  const [form] = Form.useForm<StepUpFormValues>();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const tenantId = useMemo(() => getTenantId() ?? resolveDefaultTenantId(), []);
  const showDevOtp = isNonProd();

  const handleSubmit = async (): Promise<void> => {
    const values = await form.validateFields();
    setSubmitting(true);
    setError(null);
    try {
      const { stepUpSession } = await import('../../services/authApi');
      const result = await stepUpSession({
        tenantId,
        ...(values.password?.trim() ? { password: values.password.trim() } : {}),
        ...(values.otpCode?.trim() ? { otpCode: values.otpCode.trim() } : {}),
      });
      if (!result.success || !result.accessToken) {
        setError(result.error ?? '추가 인증에 실패했습니다.');
        return;
      }

      setJWTToken(result.accessToken);
      if (result.refreshToken) {
        setRefreshToken(result.refreshToken);
      }
      emitStepUpMfaCompleted();
      onSucceeded?.();
      form.resetFields();
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title="추가 인증"
      open={open}
      okText="인증"
      cancelText="취소"
      confirmLoading={submitting}
      onCancel={onClose}
      onOk={() => void handleSubmit()}
      destroyOnHidden
    >
      <Form form={form} layout="vertical" requiredMark={false}>
        <Text type="secondary">민감 작업을 계속하려면 비밀번호를 다시 입력하세요.</Text>
        <Form.Item name="password" label="비밀번호">
          <Input.Password autoComplete="current-password" />
        </Form.Item>

        {showDevOtp ? (
          <Form.Item name="otpCode" label="개발 OTP 코드 (비프로덕션)">
            <Input inputMode="numeric" />
          </Form.Item>
        ) : null}

        {error ? <Alert type="error" message={error} showIcon /> : null}
      </Form>
    </Modal>
  );
}

export default StepUpMfaModal;
